import {utils} from '@wallaby-ui/core';
import {find, isArray, isEmpty} from 'lodash';
import {IntlShape} from 'react-intl';

import Filter from './filter';
import {FilterOptions} from '../filter_types';
import {
  FILTER_TYPE_AGENS,
  FILTER_TYPE_MULTI_SELECT,
  FILTER_TYPE_ORGANIZATION,
} from 'src/js/lib/filtering/filters_definitions_constants';
import {FilterSchema} from 'src/js/model/filters_config';

class MultiSelectFilter extends Filter {
  constructor(filterData) {
    super(filterData);
    this.type = filterData.type || FILTER_TYPE_MULTI_SELECT;
    this.value = filterData.value;
  }

  static fromString(serializedFilter: string) {
    const nameValueSepIdx = serializedFilter.indexOf(':');
    const property = serializedFilter.slice(0, nameValueSepIdx);

    const stringValues = serializedFilter.slice(nameValueSepIdx + 1);
    if (stringValues === '*') {
      return new MultiSelectFilter({property, value: null});
    }
    const quotedValues = stringValues.split(',');

    const values = quotedValues.map((value) => value.trim());
    return new MultiSelectFilter({property, value: values});
  }

  static fromSchema(schemaNode) {
    return new MultiSelectFilter({property: schemaNode.getName()});
  }

  clone() {
    return new MultiSelectFilter({
      type: this.type,
      property: this.property,
      value: {...this.value},
      id: this.id,
    });
  }

  getValue() {
    return this.value;
  }

  getValueToString(_options) {
    if (this.value != null && isArray(this.value)) {
      return `${this.value.map((val) => `${val}`).join(',')}`;
    }
    if (this.value != null && !isArray(this.value)) {
      return `${this.value}`;
    }
    return undefined;
  }

  toString() {
    if (this.value != null && isArray(this.value)) {
      return `${this.property}:${this.value.map((val) => `${val}`).join(',')}`;
    }
    if (this.value != null && !isArray(this.value)) {
      return `${this.property}:${this.value}`;
    }
    return `${this.property}:*`;
  }

  toComponentString(
    filterSchema: FilterSchema,
    translateService: IntlShape,
    options?: FilterOptions
  ) {
    const node = filterSchema.getFilterByName(this.property);
    const messageId = node.getLabel();
    const label = utils.TranslateUtils.translate(messageId, translateService);
    let nodeValue;
    if (!isEmpty(node.getChildrenIds())) {
      nodeValue = this.value.map((item) =>
        utils.TranslateUtils.translate(
          node.getChildLabel(filterSchema, item),
          translateService
        )
      );
    } else if (node.getType() === FILTER_TYPE_ORGANIZATION) {
      if (isArray(this.value)) {
        nodeValue = this.value.map((item) => {
          const itemOption =
            find(
              options?.organizations,
              (option) => option.id.toString() === item.toString()
            ) || {};
          return itemOption.organizationLongName || item;
        });
      } else {
        const itemOption =
          find(
            options?.organizations,
            (option) => option.id.toString() === this.value
          ) || {};
        nodeValue = itemOption.organizationLongName || this.value;
      }
    } else if (node.getType() === FILTER_TYPE_AGENS) {
      nodeValue = this.value.map((item) => {
        const itemOption =
          find(options?.agens, (pathogen) => {
            return pathogen.id.toString() === item.toString();
          }) || {};
        const translatedLabel = utils.TranslateUtils.translate(
          itemOption?.label,
          translateService
        );
        return translatedLabel || item;
      });
    } else if (options?.isAsync) {
      nodeValue = this.value.map((item) => {
        const itemOption =
          find(options?.mappedEnumOptions, (option) => option.value === item) ||
          {};
        return itemOption.label || item;
      });
    } else if (options?.isHierarchy) {
      const nodeAutocompleteValues = node.getHierarchyType()
        ? options?.autocompleteValues?.[node.getHierarchyType()]
        : [];
      nodeValue = this.value.map((item) => {
        let resultLabel = item;
        if (!isEmpty(nodeAutocompleteValues)) {
          const itemOption =
            find(
              nodeAutocompleteValues.getMappedOptions(),
              (option) => option.getValue() === item
            ) || {};
          if (!isEmpty(itemOption)) {
            resultLabel = itemOption.getLabel();
          }
        }
        return resultLabel;
      });
    } else {
      nodeValue = this.value
        .map(
          (itemValue) =>
            options?.hierarchyChildrenLabels[itemValue] || itemValue
        )
        .join(' ; ');
    }
    return `${label}: ${nodeValue}`;
  }
}

export default MultiSelectFilter;
